import clone from "clone";
import React from "react";
import { SurveySegment, SurveyQuestion, ISelectionStats, surveyHelper, segmentListIsInLists, ScoredSelectionStats } from "../../../../../../../amorg-common";
import { surveyQuestionHelper } from "../../../../../../../amorg-common/helpers/surveyQuestionHelper";
import { ChipPanel } from "../../../../../../../designSystem/Chip";
import { Flex } from "../../../../../../../designSystem/compositeComponents/FlexHelpers";
import { SurveyQuestionAnswerScoreRadialChart } from "../../../../../../../designSystem/graphs/SurveyAnswerScoreRadialChart";
import { OnOff } from "../../../../../../../designSystem/inputs/OnOff";
import { Table, TableHeaderWithSort } from "../../../../../../../designSystem/Table";
import { Em } from "../../../../../../../designSystem/typography/Em";
import { amorgLocalStorage } from "../../../../../../../utils/amorgLocalStorage";
import { PrintButtonWithContent } from "../../../../../../_components/PrintButtonWithContent";
import { WorkplaceAssessmentComponentProps } from "../../_components/WorkplaceAssessmentComponentProps";
import { SegmentListChipList } from "../../_components/SegmentListChipList";
import { QuestionSurveyAnswerRatioBar } from "../_components/SurveyAnswerRatioBar";
import { TooltipTheOneWrapper } from "../../../../../../../designSystem/TooltipTheOne";
import { ToggleChip } from "../../../../../../../designSystem/Chip";
import { PageSection } from "../../../../../../../designSystem/PageSection";
import { Span14 } from "../../../../../../../designSystem/typography/Body";
import { EditableSegmentPanel } from "../../../../../../_components/activities/EditableSegmentPanel";


type SegmentQuestionStats = {
    segments: SurveySegment[],
    questionIndex:number,
    question:SurveyQuestion,
    stats:ISelectionStats
}

type DetailsReportPanelProps = WorkplaceAssessmentComponentProps & {
    setHeaderButtons: React.Dispatch<React.SetStateAction<React.ReactNode>>,
};

export function DetailsReportPanel(props:DetailsReportPanelProps):JSX.Element
{
    const [segmentFilter, setSegmentFilter] = React.useState<SurveySegment[][]>([]);
    const [questionFilter, setQuestionFilter] = React.useState<{questionTitle:string, show:boolean}[]>([]);
    const [sortColumn, setSortColumn] = React.useState<'question' | 'score'>('question');
    const [sortDirection, setSortDirection] = React.useState<'asc' | 'desc'>('asc');
    
    React.useEffect(() => {
        if(!props.survey)
            return;

        const filter = amorgLocalStorage.surveySegmentation.filter2.readFromStorage(props.survey.id);
        setSegmentFilter(filter);

        const questions = surveyHelper.getAllSurveyQuestions(props.survey);
        setQuestionFilter(questions.map(q => ({ questionTitle: q.question.title, show: true })));

    }, [props.survey?.id || '']);

    const dataSet = React.useMemo(() => {
        if(!props.survey || !props.surveyReport)
            return [];

        const allQuestions = surveyHelper.getAllSurveyQuestions(props.survey);

        const data:SegmentQuestionStats[] = [];
        for(const sr of props.surveyReport.selectionReports)
        {
            for(let questionIndex = 0; questionIndex < sr.questionIndexedSummaries.length; questionIndex++)
            {
                data.push({
                    segments: sr.segments,
                    questionIndex,
                    question: allQuestions[questionIndex].question,
                    stats: surveyQuestionHelper.toSelectionStats(
                        allQuestions[questionIndex].question,
                        sr.questionIndexedSummaries[questionIndex].selectionSummary
                    )
                });
            }
        }
        return data;

    }, [props.surveyReport]);

    const filterOptions = React.useMemo(() => {
        if(!props.survey || !props.surveyReport)
            return [];

        return props.surveyReport.selectionReports.map(sr => ({ segments: sr.segments, answerCount:0 }));
    }, [props.surveyReport]);

    const filteredSortedDataSet = React.useMemo(() => {
        return dataSet
            .filter(d => dataSet.length > 1 && dataSet[0].segments.length > 0
                ? segmentListIsInLists(segmentFilter, d.segments)
                : true)
            .filter(d => questionFilter.length > 0 
                ? questionFilter[d.questionIndex].show 
                : true
            )
            .sort((a,b) => {
                if(sortColumn === 'question')
                {
                    return sortDirection === 'asc' 
                        ? a.questionIndex - b.questionIndex
                        : b.questionIndex - a.questionIndex;
                }
                else
                {
                    // Both are scored
                    if(a.stats instanceof ScoredSelectionStats && b.stats instanceof ScoredSelectionStats)
                    {
                        return sortDirection === 'asc'
                            ? a.stats.score.agreePlusPct - b.stats.score.agreePlusPct
                            : b.stats.score.agreePlusPct - a.stats.score.agreePlusPct;
                    }
                    // A is scored, B is not
                    else if((a.stats instanceof ScoredSelectionStats) && !(b.stats instanceof ScoredSelectionStats))
                    {
                        // A is sorted highest
                        return sortDirection === 'asc' ? -1 : 1;
                    }
                    // B is scored, A is not
                    else if(!(a.stats instanceof ScoredSelectionStats) && (b.stats instanceof ScoredSelectionStats))
                    {
                        // B is sorted highest
                        return sortDirection === 'asc' ? 1 : -1;
                    }
                    // Both are not scored
                    else
                    {
                        return 0;
                    }
                }
    })},[dataSet, segmentFilter, questionFilter, sortColumn, sortDirection]);

    React.useEffect(() => {
        props.setHeaderButtons(
            <PrintButtonWithContent documentTitle={`${props.activity.title} - Detaljevisning`}>
                {() =>
                    <DetailsReportContent data={filteredSortedDataSet} />
                }
            </PrintButtonWithContent>
        );
    }, [filteredSortedDataSet, props.setHeaderButtons, props.activity.title]);

    React.useEffect(() => {
        return () => props.setHeaderButtons(undefined);
    }, [props.setHeaderButtons]);


    function handleSegmentFilterChanged(newFilter: SurveySegment[][]) {
        if(!props.survey)
            return;
        
        setSegmentFilter(newFilter);
        amorgLocalStorage.surveySegmentation.filter2.writeToStorage(props.survey.id, newFilter);
    }

    function handleQuestionFilterChanged(index:number) {
        const cloned = clone(questionFilter);
        cloned[index].show = !cloned[index].show;
        setQuestionFilter(cloned);
    }

    function handleClickToggleAllQuestions() {
        const allSelected = questionFilter.every(q => q.show);
        const cloned = clone(questionFilter);
        cloned.forEach(q => q.show = !allSelected);
        setQuestionFilter(cloned);
    }

    function truncateString(str: string): string {
        if (str.length <= 24) {
            return str;
        }
        return str.substring(0, 21) + '...';
    }

    const hasSegments = dataSet.length > 0 && dataSet[0].segments.length > 0;

    return (
        <>
            {hasSegments &&
                <PageSection headerStyleVariant="card" contentStyleVariant="card"
                    header={{
                        title: 'Valgte segmenter',
                        defaultCollapsed: true
                    }}>
                    <EditableSegmentPanel segmentLists={filterOptions} onSelectionChange={handleSegmentFilterChanged} selection={segmentFilter} />
                </PageSection>
            }

            <PageSection headerStyleVariant="card" contentStyleVariant="card"
                header={{
                    title: 'Valgte spørgsmål',
                    defaultCollapsed: false
                }}>

                <Flex alignItems="center" gap={4}>
                    <span style={{ fontSize: '14px' }}>Vælg alle</span>
                    <OnOff size="small" active={questionFilter.every(s => s.show)} onClick={handleClickToggleAllQuestions} />
                </Flex>

                <ChipPanel>
                    {questionFilter.map((q, index) =>
                        <TooltipTheOneWrapper key={index} tooltipContent={`Spørgsmål #${index+1} - ${q.questionTitle}`}>
                            <ToggleChip key={index} onClick={() => handleQuestionFilterChanged(index)} selected={q.show}>
                                {truncateString(`#${index+1} ${q.questionTitle}`)}
                            </ToggleChip>
                        </TooltipTheOneWrapper>
                    )}
                </ChipPanel>
            </PageSection>
            
            <div>Fordeling af besvarelser for pågældende segment- og spørgsmål</div>
            <Table>
                <thead>
                    <tr>
                        {hasSegments &&
                            <th>Segmenter</th>
                        }
                        <TableHeaderWithSort
                            text="Spørgsmål"
                            ascending={sortColumn === 'question' && sortDirection === 'asc'}
                            isActiveSort={sortColumn === 'question'}
                            onClick={() => {
                                if (sortColumn === 'question') {
                                    setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                                } else {
                                    setSortColumn('question');
                                    setSortDirection('asc');
                                }
                            }} />
                        <TableHeaderWithSort
                            text="Score"
                            ascending={sortColumn === 'score' && sortDirection === 'asc'}
                            isActiveSort={sortColumn === 'score'}
                            onClick={() => {
                                if (sortColumn === 'score') {
                                    setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                                } else {
                                    setSortColumn('score');
                                    setSortDirection('asc');
                                }
                            }} />
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    {filteredSortedDataSet.map((d, index) =>  
                        <ResultTableRow key={index} data={d} />
                    )}
                </tbody>
            </Table>
        </>
    );
}

type DetailsReportContentProps = {
    data: SegmentQuestionStats[],
};

function DetailsReportContent(props: DetailsReportContentProps): JSX.Element
{
    return (
        <>
            <div>Gennemsnitligt antal besvarelser hvor der er valgt <Em>enig</Em> eller <Em>meget enig</Em> for pågældende segment- og spørgsmål</div>
            <Table>
                <thead>
                    <tr>
                        <th>Segmenter</th>
                        <th>Spørgsmål</th>
                        <th>Score</th>
                    </tr>
                </thead>

                <tbody>
                    {props.data.map((d, index) => 
                        <ResultTableRow key={index} data={d} />
                    )}
                </tbody>
            </Table>
        </>
    );
}

function ResultTableRow(props: { data: SegmentQuestionStats }): JSX.Element
{
    return (
    <tr>
            {props.data.segments.length > 0 &&
                <td style={{maxWidth:'33%'}}>
                    <SegmentListChipList segments={props.data.segments} identifier={''} />
                </td>
            }
            <td style={{maxWidth:'33%'}}><Span14>#{props.data.questionIndex+1} {props.data.question.title}</Span14></td>
            <td style={{minWidth:'40%'}}>
                <Flex gap={4} alignItems="center">
                    <SurveyQuestionAnswerScoreRadialChart question={props.data.question} stats={props.data.stats} small />
                    <QuestionSurveyAnswerRatioBar stats={props.data.stats} question={props.data.question} small />
                </Flex>
            </td>
        </tr>
    );
}